import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiClock, FiInfo, FiCheck, FiCreditCard, FiArrowLeft } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { getImageUrl, getPlaceholderUrl } from '../../utils/imageUrl';

const CustomerBook = () => {
  const [services, setServices] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isDownPayment, setIsDownPayment] = useState(false);
  const [downPaymentPercent, setDownPaymentPercent] = useState(50);
  const [downPaymentAmount, setDownPaymentAmount] = useState(0);
  const [runtimeMidtransClientKey, setRuntimeMidtransClientKey] = useState(
    process.env.REACT_APP_MIDTRANS_CLIENT_KEY || ''
  );
  const [runtimeMidtransIsProduction, setRuntimeMidtransIsProduction] = useState(
    process.env.REACT_APP_MIDTRANS_IS_PRODUCTION === 'true'
  );
  const [isSnapScriptReady, setIsSnapScriptReady] = useState(Boolean(window.snap));

  const [formData, setFormData] = useState({
    serviceId: '',
    mechanicId: '',
    date: '',
    time: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleLicensePlate: '',
    customerName: '',
    notes: '',
    selectedServices: [], // Array to store multiple selected services
  });

  const navigate = useNavigate();
  const isMidtransProduction = runtimeMidtransIsProduction;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch services and available mechanics
        const [servicesRes, mechanicsRes] = await Promise.all([
          API.get('/api/service'),
          API.get('/api/mechanic'),
        ]);

        setServices(servicesRes.data);

        // Format mechanics data
        const formattedMechanics = mechanicsRes.data.map(mechanic => ({
          id: mechanic.id,
          name: mechanic.name,
          rating: mechanic.rating || 4.5
        }));

        setMechanics(formattedMechanics);

        // Generate available times
        const times = [];
        for (let hour = 8; hour <= 16; hour++) {
          if (hour !== 12) { // Skip lunch hour
            times.push({
              value: `${hour}:00`,
              label: `${hour}:00`,
            });
            if (hour !== 16) {
              times.push({
                value: `${hour}:30`,
                label: `${hour}:30`,
              });
            }
          }
        }
        setAvailableTimes(times);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setServices([]);
        setMechanics([]);

        generateTimes();
      } finally {
        setLoading(false);
      }
    };

    // Helper function to generate times
    const generateTimes = () => {
      // Generate available times
      const times = [];
      for (let hour = 8; hour <= 16; hour++) {
        if (hour !== 12) { // Skip lunch hour
          times.push({
            value: `${hour}:00`,
            label: `${hour}:00`,
          });
          if (hour !== 16) {
            times.push({
              value: `${hour}:30`,
              label: `${hour}:30`,
            });
          }
        }
      }
      setAvailableTimes(times);
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchMidtransConfig = async () => {
      try {
        const response = await API.get('/api/booking/payments/midtrans/config');

        if (response.data?.client_key) {
          setRuntimeMidtransClientKey(response.data.client_key);
        }

        if (typeof response.data?.is_production === 'boolean') {
          setRuntimeMidtransIsProduction(response.data.is_production);
        }
      } catch (configError) {
        console.error('Error fetching Midtrans config:', configError);
      }
    };

    fetchMidtransConfig();
  }, []);

  useEffect(() => {
    if (!runtimeMidtransClientKey) {
      return undefined;
    }

    const scriptId = 'midtrans-snap-script';
    const scriptSrc = isMidtransProduction
      ? 'https://app.midtrans.com/snap/snap.js'
      : 'https://app.sandbox.midtrans.com/snap/snap.js';
    const existingScript = document.getElementById(scriptId);

    if (
      existingScript &&
      existingScript.getAttribute('data-client-key') === runtimeMidtransClientKey
    ) {
      setIsSnapScriptReady(Boolean(window.snap));
      return undefined;
    }

    if (existingScript && existingScript.parentNode) {
      existingScript.parentNode.removeChild(existingScript);
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.src = scriptSrc;
    script.setAttribute('data-client-key', runtimeMidtransClientKey);
    script.async = true;
    script.onload = () => setIsSnapScriptReady(true);
    script.onerror = () => setIsSnapScriptReady(false);
    setIsSnapScriptReady(false);
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [isMidtransProduction, runtimeMidtransClientKey]);

  const ensureSnapScriptLoaded = async (clientKeyOverride) => {
    const effectiveClientKey = clientKeyOverride || runtimeMidtransClientKey;

    if (!effectiveClientKey) {
      throw new Error('Midtrans Client Key belum tersedia. Restart frontend atau deploy ulang agar env terbaru terbaca.');
    }

    if (window.snap) {
      setIsSnapScriptReady(true);
      return;
    }

    await new Promise((resolve, reject) => {
      const scriptId = 'midtrans-snap-script';
      const scriptSrc = isMidtransProduction
        ? 'https://app.midtrans.com/snap/snap.js'
        : 'https://app.sandbox.midtrans.com/snap/snap.js';
      const existingScript = document.getElementById(scriptId);

      if (
        existingScript &&
        existingScript.getAttribute('data-client-key') === effectiveClientKey
      ) {
        existingScript.addEventListener('load', () => {
          setIsSnapScriptReady(true);
          resolve();
        }, { once: true });
        existingScript.addEventListener('error', () => {
          setIsSnapScriptReady(false);
          reject(new Error('Gagal memuat script Midtrans Snap.'));
        }, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'text/javascript';
      script.src = scriptSrc;
      script.setAttribute('data-client-key', effectiveClientKey);
      script.async = true;
      script.onload = () => {
        setIsSnapScriptReady(true);
        resolve();
      };
      script.onerror = () => {
        setIsSnapScriptReady(false);
        reject(new Error('Gagal memuat script Midtrans Snap.'));
      };

      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }

      setIsSnapScriptReady(false);
      document.body.appendChild(script);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError('');
  };

  const handleDownPaymentChange = (e) => {
    setIsDownPayment(e.target.checked);
    calculateDownPaymentAmount(downPaymentPercent);
  };

  const handleDownPaymentPercentChange = (e) => {
    const percent = parseInt(e.target.value, 10);
    setDownPaymentPercent(percent);
    calculateDownPaymentAmount(percent);
  };

  const calculateDownPaymentAmount = (percent) => {
    const totalPrice = getTotalPrice();
    const amount = (totalPrice * percent) / 100;
    setDownPaymentAmount(amount);
  };

  const verifyInputData = () => {
    // Periksa layanan yang dipilih
    if (formData.selectedServices.length === 0) {
      setError('Silakan pilih minimal satu layanan');
      return false;
    }

    // Verifikasi objek layanan
    for (const serviceId of formData.selectedServices) {
      const service = services.find(s => s.id.toString() === serviceId.toString());
      if (!service) {
        console.error('Selected service not found in available services:', serviceId);
        setError('Salah satu layanan yang dipilih tidak valid');
        return false;
      }
      console.log('Verified service:', service.id, service.name);
    }

    // Periksa nama pelanggan
    if (!formData.customerName) {
      setError('Silakan isi nama pelanggan');
      return false;
    }

    // Periksa tanggal & waktu
    if (!formData.date) {
      setError('Silakan pilih tanggal');
      return false;
    }
    if (!formData.time) {
      setError('Silakan pilih waktu');
      return false;
    }

    // Periksa informasi kendaraan
    if (!formData.vehicleMake || !formData.vehicleModel || !formData.vehicleYear || !formData.vehicleLicensePlate) {
      setError('Silakan isi informasi kendaraan dengan lengkap');
      return false;
    }

    return true;
  };

  const handleBookingClick = (e) => {
    e.preventDefault();

    // Validasi form menggunakan fungsi verifikasi
    if (!verifyInputData()) {
      return;
    }

    // Hitung jumlah DP berdasarkan total harga
    calculateDownPaymentAmount(downPaymentPercent);

    // Show payment form instead of submitting
    setShowPaymentForm(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    setFormSubmitting(true);
    setError('');

    try {
      // Check if token exists and is valid
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Sesi anda telah berakhir. Silakan login kembali.');
        navigate('/login');
        return;
      }

      // First, check for existing vehicles or create a new one
      let vehicleId;

      try {
        // Try to get existing vehicles
        const vehiclesResponse = await API.get('/api/vehicle/my-vehicles');

        // Look for a matching vehicle
        const existingVehicle = vehiclesResponse.data.find(
          vehicle =>
            vehicle.make.toLowerCase() === formData.vehicleMake.toLowerCase() &&
            vehicle.model.toLowerCase() === formData.vehicleModel.toLowerCase() &&
            vehicle.year.toString() === formData.vehicleYear.toString() &&
            vehicle.license_plate.toLowerCase() === formData.vehicleLicensePlate.toLowerCase()
        );

        if (existingVehicle) {
          vehicleId = existingVehicle.id;
          console.log('Using existing vehicle with ID:', vehicleId);
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        // Continue to create a new vehicle
      }

      // If no existing vehicle found, create a new one
      if (!vehicleId) {
        try {
          console.log('Creating new vehicle with data:', {
            make: formData.vehicleMake,
            model: formData.vehicleModel,
            year: formData.vehicleYear,
            license_plate: formData.vehicleLicensePlate
          });

          const vehicleResponse = await API.post('/api/vehicle', {
            make: formData.vehicleMake,
            model: formData.vehicleModel,
            year: formData.vehicleYear,
            license_plate: formData.vehicleLicensePlate,
          });

          console.log('Vehicle creation response:', vehicleResponse.data);

          // Cek apakah respons memiliki struktur yang diharapkan
          if (vehicleResponse.data && vehicleResponse.data.vehicle && vehicleResponse.data.vehicle.id) {
            vehicleId = vehicleResponse.data.vehicle.id;
            console.log('Created new vehicle with ID:', vehicleId, 'of type:', typeof vehicleId);
          } else if (vehicleResponse.data && vehicleResponse.data.id) {
            // Alternative response format
            vehicleId = vehicleResponse.data.id;
            console.log('Created new vehicle with ID (alt format):', vehicleId);
          } else {
            console.error('Unexpected vehicle creation response format:', vehicleResponse.data);
            throw new Error('Failed to get vehicle ID from response');
          }
        } catch (error) {
          console.error('Error creating vehicle:', error);

          // Cek apakah error disebabkan oleh token yang tidak valid
          if (error.response &&
            (error.response.status === 401 ||
              (error.response.status === 403 &&
                error.response.data.message === 'Invalid or expired token'))) {
            setError('Sesi anda telah berakhir. Silakan login kembali.');
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          } else {
            setError('Gagal membuat kendaraan: ' + (error.response?.data?.message || error.message));
          }

          setFormSubmitting(false);
          return;
        }
      }

      console.log('Using vehicle with ID:', vehicleId, 'of type:', typeof vehicleId);

      // Buat perubahan untuk membuat booking satu per satu, bukan dengan Promise.all
      let successCount = 0;
      let lastError = null;
      let snapToken = null;
      let redirectUrl = null;
      let paymentClientKey = runtimeMidtransClientKey;
      const batchId = `BATCH-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

      const openSnapPayment = async (currentSnapToken, clientKeyOverride) => {
        await ensureSnapScriptLoaded(clientKeyOverride);

        return new Promise((resolve, reject) => {
          if (!window.snap) {
            reject(new Error('Midtrans Snap belum siap. Coba ulang beberapa detik lagi.'));
            return;
          }

          window.snap.pay(currentSnapToken, {
            onSuccess: (result) => resolve(result),
            onPending: (result) => resolve(result),
            onError: (snapError) => reject(new Error(snapError?.status_message || 'Pembayaran Midtrans gagal diproses.')),
            onClose: () => resolve({ closed: true, transaction_status: 'pending' })
          });
        });
      };

      // Proses booking satu per satu
      for (const [index, serviceId] of formData.selectedServices.entries()) {
        try {
          console.log('Processing service ID:', serviceId, 'of type:', typeof serviceId);

          // Pastikan service_id dan vehicle_id valid
          if (!serviceId || !vehicleId) {
            console.error('Missing service_id or vehicle_id:', { serviceId, vehicleId });
            lastError = 'ID layanan atau kendaraan tidak tersedia';
            continue; // Skip this booking
          }

          // Konversi ID ke integer
          let serviceIdInt, vehicleIdInt;

          // Handle different formats of IDs
          try {
            // First attempt: Parse as integer
            serviceIdInt = parseInt(serviceId, 10);
            vehicleIdInt = parseInt(vehicleId, 10);

            // If parsing fails or results in NaN, try alternate methods
            if (isNaN(serviceIdInt)) {
              // Try to extract numeric part if it's a string like "service-123"
              const numericPart = serviceId.toString().match(/\d+/);
              if (numericPart) {
                serviceIdInt = parseInt(numericPart[0], 10);
              }
            }

            if (isNaN(vehicleIdInt)) {
              // Try to extract numeric part if it's a string like "vehicle-123"
              const numericPart = vehicleId.toString().match(/\d+/);
              if (numericPart) {
                vehicleIdInt = parseInt(numericPart[0], 10);
              }
            }

            console.log('After advanced conversion, IDs:', { serviceIdInt, vehicleIdInt });
          } catch (error) {
            console.error('Error during ID conversion:', error);
          }

          // Final verification of ID conversion results
          if (isNaN(serviceIdInt) || isNaN(vehicleIdInt)) {
            console.error('Failed to convert service_id or vehicle_id to valid numbers:', { serviceId, vehicleId });
            lastError = 'ID layanan atau kendaraan tidak valid';
            continue; // Skip this booking
          }

          // Format tanggal sebagai string YYYY-MM-DD
          const dateStr = formData.date;

          // Format waktu sebagai string HH:MM:SS
          let timeStr = formData.time;
          if (!timeStr) {
            timeStr = "08:00:00"; // Default time if missing
          } else if (!timeStr.includes(':')) {
            timeStr = `${timeStr}:00:00`;
          } else if (timeStr.split(':').length === 2) {
            timeStr = `${timeStr}:00`; // Add seconds
          }

          const notesStr = formData.notes || '';

          // Log untuk debugging
          console.log('Booking data raw values:', {
            serviceId: serviceIdInt,
            vehicleId: vehicleIdInt,
            date: dateStr,
            time: timeStr,
            notes: notesStr
          });

          // Buat objek yang akan dikirim ke server
          const requestData = {
            service_id: serviceIdInt,
            vehicle_id: vehicleIdInt,
            date: dateStr,
            time: timeStr,
            notes: notesStr,
            payment_method: 'snap',
            customer_name: formData.customerName, // Tambahkan nama pelanggan
            is_down_payment: isDownPayment, // Tambahkan flag pembayaran DP
            down_payment_percent: isDownPayment ? downPaymentPercent : 0, // Persentase DP
            down_payment_amount: isDownPayment ? downPaymentAmount : 0, // Jumlah DP
            booking_batch_id: batchId,
            create_payment: index === 0,
            total_payment_amount: getTotalPrice()
          };

          console.log('Sending customer name:', formData.customerName);
          console.log('Payment details:', {
            is_down_payment: isDownPayment,
            down_payment_percent: downPaymentPercent,
            down_payment_amount: downPaymentAmount,
            total_price: getTotalPrice()
          });

          // Tambahkan mechanic_id jika dipilih
          if (formData.mechanicId) {
            requestData.mechanic_id = parseInt(formData.mechanicId, 10);
            console.log('Adding mechanic_id to request:', requestData.mechanic_id, 'from', formData.mechanicId);
          }

          console.log('Sending booking data:', JSON.stringify(requestData));

          try {
            // Kirim request ke server
            const response = await API.post('/api/booking', requestData);
            console.log('Booking response:', response.data);

            if (response.data?.payment?.snap_token && !snapToken) {
              snapToken = response.data.payment.snap_token;
            }

            if (response.data?.payment?.redirect_url && !redirectUrl) {
              redirectUrl = response.data.payment.redirect_url;
            }

            if (response.data?.payment?.client_key) {
              paymentClientKey = response.data.payment.client_key;
              setRuntimeMidtransClientKey(response.data.payment.client_key);
            }

            successCount++;
          } catch (bookingError) {
            const errorMsg = bookingError.response?.data?.message || bookingError.message;
            console.error('Error creating booking:', errorMsg);

            // Cek apakah error disebabkan oleh token yang tidak valid
            if (bookingError.response &&
              (bookingError.response.status === 401 ||
                (bookingError.response.status === 403 &&
                  bookingError.response.data.message === 'Invalid or expired token'))) {
              lastError = 'Sesi anda telah berakhir. Silakan login kembali.';
              setTimeout(() => {
                navigate('/login');
              }, 2000);
              break; // Keluar dari loop jika token tidak valid
            }

            // Jika ada error spesifik tentang data yang diharapkan, tambahkan logging tambahan
            if (errorMsg && errorMsg.includes && errorMsg.includes('required')) {
              console.error('Required fields missing in the request');
            }

            lastError = errorMsg;
            // Lanjutkan ke booking berikutnya walaupun ada error
          }
        } catch (error) {
          const errorMsg = error.response?.data?.message || error.message;
          console.error('Error processing service:', errorMsg);
          lastError = errorMsg;
        }
      }

      // Tampilkan pesan sukses jika setidaknya satu booking berhasil
      if (successCount > 0) {
        if (snapToken) {
          try {
            const snapResult = await openSnapPayment(snapToken, paymentClientKey);
            console.log('Midtrans payment result:', snapResult);

            if (snapResult?.closed) {
              lastError = 'Booking berhasil dibuat, tetapi popup pembayaran ditutup sebelum selesai. Status pembayaran tetap pending.';
            }
          } catch (snapError) {
            console.error('Error opening Midtrans Snap:', snapError);

            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }

            throw snapError;
          }
        } else if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }

        setFormSubmitting(false);
        setBookingSuccess(true);
        setTimeout(() => {
          navigate('/customer/bookings', { state: { fromBooking: true } });
        }, 3000);
      } else {
        // Tampilkan pesan error jika semua booking gagal
        setError('Gagal membuat booking: ' + (lastError || 'Silakan coba lagi nanti.'));
        setFormSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating booking:', error);

      // Cek apakah error disebabkan oleh token yang tidak valid
      if (error.response &&
        (error.response.status === 401 ||
          (error.response.status === 403 &&
            error.response.data.message === 'Invalid or expired token'))) {
        setError('Sesi anda telah berakhir. Silakan login kembali.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError('Gagal membuat booking. ' + (error.response?.data?.message || 'Silakan coba lagi nanti.'));
      }

      setFormSubmitting(false);
    }
  };

  const handleBackToBooking = () => {
    setShowPaymentForm(false);
  };

  const handleServiceSelection = (serviceId) => {
    // Toggle service selection
    const serviceIdStr = serviceId.toString();
    let updatedSelectedServices = [...formData.selectedServices];

    if (updatedSelectedServices.includes(serviceIdStr)) {
      // Remove if already selected
      updatedSelectedServices = updatedSelectedServices.filter(id => id !== serviceIdStr);
    } else {
      // Add if not selected
      updatedSelectedServices.push(serviceIdStr);
    }

    setFormData({
      ...formData,
      selectedServices: updatedSelectedServices
    });

    setError('');
  };

  const getTotalPrice = () => {
    return formData.selectedServices.reduce((total, serviceId) => {
      const service = services.find(s => s.id.toString() === serviceId);
      // Pastikan price adalah angka yang valid
      const price = service && !isNaN(parseFloat(service.price)) ? parseFloat(service.price) : 0;
      return total + price;
    }, 0);
  };

  const getTotalDuration = () => {
    return formData.selectedServices.reduce((total, serviceId) => {
      const service = services.find(s => s.id.toString() === serviceId);
      // Pastikan estimated_time adalah angka yang valid
      const duration = service && !isNaN(parseFloat(service.estimated_time)) ? parseFloat(service.estimated_time) : 0;
      return total + duration;
    }, 0);
  };

  const getSelectedServices = () => {
    return services.filter(service =>
      formData.selectedServices.includes(service.id.toString())
    );
  };

  const formatPrice = (price) => {
    // Pastikan price adalah angka yang valid
    if (price === undefined || price === null || isNaN(price)) {
      price = 0;
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" userName="Pelanggan">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="customer" userName="Pelanggan">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary">
          {showPaymentForm ? 'Pembayaran Booking' : 'Buat Booking Baru'}
        </h1>
        <p className="text-gray-600">
          {showPaymentForm
            ? 'Silakan cek ringkasan pembayaran lalu lanjutkan ke Midtrans Snap'
            : 'Silakan isi formulir di bawah untuk membuat booking servis'}
        </p>
      </div>

      {bookingSuccess ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 p-3">
              <FiCheck className="text-green-500" size={30} />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-green-800 mb-2">Booking Berhasil!</h2>
          <p className="text-green-700 mb-4">
            Booking berhasil dibuat. Status pembayaran akan diperbarui otomatis setelah Midtrans mengonfirmasi transaksi Anda.
          </p>
        </div>
      ) : showPaymentForm ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <button
                onClick={handleBackToBooking}
                className="flex items-center text-blue-600 mb-4 hover:text-blue-800"
              >
                <FiArrowLeft className="mr-1" /> Kembali ke Form Booking
              </button>

              <h2 className="text-lg font-semibold text-secondary mb-4">Informasi Pembayaran</h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handlePaymentSubmit}>
                <div className="mb-6">
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <div className="flex items-start">
                      <FiCreditCard className="mr-3 mt-0.5 text-primary" />
                      <div>
                        <div className="font-medium text-gray-900">Metode pembayaran disediakan langsung oleh Midtrans</div>
                        <div className="mt-1 text-sm text-gray-600">
                          Setelah klik tombol bayar, Midtrans Snap akan menampilkan semua metode pembayaran yang aktif di akun merchant Anda.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="downPaymentOption"
                      checked={isDownPayment}
                      onChange={handleDownPaymentChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="downPaymentOption" className="ml-2 block text-gray-700 text-sm font-medium">
                      Bayar DP (Down Payment)
                    </label>
                  </div>

                  {isDownPayment && (
                    <div className="pl-6 border-l-2 border-blue-100 mb-4">
                      <div className="mb-3">
                        <label className="block text-gray-700 text-sm font-medium mb-2">
                          Persentase DP
                        </label>
                        <div className="flex items-center">
                          <input
                            type="range"
                            min="10"
                            max="90"
                            step="5"
                            value={downPaymentPercent}
                            onChange={handleDownPaymentPercentChange}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900 w-12">
                            {downPaymentPercent}%
                          </span>
                        </div>
                      </div>

                      <div className="bg-blue-50 p-3 rounded-md">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Total Harga:</span>
                          <span className="text-sm font-medium">{formatPrice(getTotalPrice())}</span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Jumlah DP ({downPaymentPercent}%):</span>
                          <span className="text-sm font-medium text-blue-700">{formatPrice(downPaymentAmount)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-blue-100 mt-2">
                          <span className="text-sm text-gray-600">Sisa Pembayaran:</span>
                          <span className="text-sm font-medium">{formatPrice(getTotalPrice() - downPaymentAmount)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <h3 className="font-medium text-gray-900 mb-3">Informasi Pembayaran</h3>

                  <div className="mb-4">
                    <div className="flex items-start">
                      <FiCreditCard className="mr-2 mt-0.5 text-primary" />
                      <div>
                        <div className="font-medium text-gray-900">Pembayaran diproses lewat Midtrans Snap</div>
                        <div className="text-sm text-gray-500">
                          Setelah klik tombol bayar, Midtrans akan menampilkan daftar metode pembayaran yang tersedia langsung dari akun merchant Anda.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-sm text-gray-500 mb-1">Jumlah yang Harus Dibayar:</div>
                    <div className="font-mono text-lg font-semibold text-green-700">
                      {isDownPayment ? formatPrice(downPaymentAmount) : formatPrice(getTotalPrice())}
                    </div>
                    {isDownPayment && (
                      <div className="mt-1 text-xs text-gray-500">
                        <span className="font-medium">DP {downPaymentPercent}%</span> dari total {formatPrice(getTotalPrice())}.
                        Sisa pembayaran: {formatPrice(getTotalPrice() - downPaymentAmount)}
                      </div>
                    )}
                  </div>

                  <div className="mb-1 text-sm text-gray-500">
                    Metode pembayaran akan dipilih di halaman atau popup Midtrans.
                  </div>
                  {formData.selectedServices.length > 1 && (
                    <p className="text-xs text-amber-700 mt-2">
                      Anda memilih lebih dari satu layanan. Midtrans akan memprosesnya sebagai satu pembayaran batch.
                    </p>
                  )}
                  {!runtimeMidtransClientKey && (
                    <p className="text-xs text-red-600 mt-2">
                      Midtrans Client Key belum terbaca dari build frontend. Sistem akan mencoba memakai key dari backend saat booking dibuat.
                    </p>
                  )}
                  {!isSnapScriptReady && runtimeMidtransClientKey && (
                    <p className="text-xs text-gray-500 mt-2">
                      Script Midtrans sedang dimuat. Jika tombol bayar belum bekerja, tunggu sebentar lalu coba lagi.
                    </p>
                  )}
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    className={`w-full bg-primary text-white py-3 px-4 rounded-lg font-medium ${formSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-dark'
                      }`}
                    disabled={formSubmitting}
                  >
                    {formSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        <span>Memproses...</span>
                      </div>
                    ) : (
                      isDownPayment ? `Bayar DP (${formatPrice(downPaymentAmount)})` : 'Bayar Lunas'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-secondary mb-4">Ringkasan Booking</h2>

              {formData.selectedServices.length > 0 ? (
                <>
                  <div className="border-b pb-4 mb-4">
                    <h3 className="font-medium text-gray-900 mb-2">Layanan Terpilih</h3>
                    {getSelectedServices().map(service => (
                      <div key={service.id} className="flex justify-between mb-2">
                        <span>{service.name}</span>
                        <span className="font-medium">{formatPrice(service.price)}</span>
                      </div>
                    ))}
                    <div className="text-sm text-gray-500 mt-3 flex items-center">
                      <FiClock className="mr-1" />
                      Total Estimasi Waktu: {getTotalDuration()} menit
                    </div>
                  </div>

                  {formData.date && formData.time && (
                    <div className="border-b pb-4 mb-4">
                      <h3 className="font-medium text-gray-900 mb-2">Jadwal</h3>
                      <div className="flex items-center text-gray-700">
                        <FiCalendar className="mr-2 text-primary" />
                        <span>
                          {formData.date && formatDate(formData.date)}
                          {formData.time && `, Pukul ${formData.time}`}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mb-2">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total Pembayaran</span>
                      <span className="text-primary">{formatPrice(getTotalPrice())}</span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mt-4 text-center">
                    Dengan melakukan booking, Anda menyetujui syarat dan ketentuan kami
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FiInfo size={32} className="mx-auto mb-2" />
                  <p>Silakan pilih layanan untuk melihat ringkasan booking</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-secondary mb-4">Informasi Booking</h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleBookingClick}>
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Pilih Layanan
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {services.map((service) => (
                      <div
                        key={service.id}
                        className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${formData.selectedServices.includes(service.id.toString())
                            ? 'border-primary ring-2 ring-primary ring-opacity-50'
                            : 'border-gray-200 hover:border-primary'
                          }`}
                        onClick={() => handleServiceSelection(service.id)}
                      >
                        <div className="relative h-40">
                          <img
                            src={service.image_url ? getImageUrl(service.image_url) : getPlaceholderUrl('Layanan+Bengkel', 400, 200)}
                            alt={service.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = getPlaceholderUrl('No+Image', 400, 200);
                            }}
                          />
                          <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 m-2 rounded-full text-sm font-medium">
                            {formatPrice(service.price)}
                          </div>
                          {formData.selectedServices.includes(service.id.toString()) && (
                            <div className="absolute top-0 left-0 bg-primary text-white p-2 m-2 rounded-full">
                              <FiCheck size={16} />
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-gray-900 text-lg">{service.name}</h3>
                            <div className="flex items-center text-sm text-gray-500">
                              <FiClock className="mr-1" />
                              {service.estimated_time} menit
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-md mb-2">
                            <p className="text-sm text-gray-600">{service.description || "Tidak ada deskripsi layanan tersedia"}</p>
                          </div>
                          <div className="flex justify-end">
                            <span className="text-xs text-primary font-medium">
                              {formData.selectedServices.includes(service.id.toString())
                                ? "✓ Layanan dipilih"
                                : "Klik untuk memilih"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Pilih Montir (Opsional)
                  </label>
                  <select
                    name="mechanicId"
                    value={formData.mechanicId}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">-- Pilih Montir --</option>
                    {mechanics.map((mechanic) => (
                      <option key={mechanic.id} value={mechanic.id}>
                        {mechanic.name} (Rating: {mechanic.rating})
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Jika tidak memilih, kami akan menugaskan montir terbaik yang tersedia
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Pilih Tanggal
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      min={new Date().toISOString().split('T')[0]} // Optional: Set minimum date to today
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Pilih Waktu
                    </label>
                    <select
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={!formData.date}
                    >
                      <option value="">-- Pilih Waktu --</option>
                      {availableTimes.map((time) => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Nama Pelanggan
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    placeholder="Masukkan nama pelanggan"
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="mb-6">
                  <h3 className="text-gray-700 text-sm font-medium mb-3">Informasi Kendaraan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-gray-700 text-xs mb-1">
                        Merk
                      </label>
                      <input
                        type="text"
                        name="vehicleMake"
                        value={formData.vehicleMake}
                        onChange={handleChange}
                        placeholder="Toyota, Honda, dll"
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-xs mb-1">
                        Model
                      </label>
                      <input
                        type="text"
                        name="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={handleChange}
                        placeholder="Avanza, Jazz, dll"
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-xs mb-1">
                        Tahun
                      </label>
                      <input
                        type="text"
                        name="vehicleYear"
                        value={formData.vehicleYear}
                        onChange={handleChange}
                        placeholder="2020"
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-xs mb-1">
                        Nomor Plat
                      </label>
                      <input
                        type="text"
                        name="vehicleLicensePlate"
                        value={formData.vehicleLicensePlate}
                        onChange={handleChange}
                        placeholder="B 1234 ABC"
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Catatan Tambahan (Opsional)
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Tambahkan catatan khusus untuk montir..."
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    rows="3"
                  ></textarea>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    className={`w-full bg-primary text-white py-3 px-4 rounded-lg font-medium ${formData.selectedServices.length === 0 ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-dark'
                      }`}
                    disabled={formData.selectedServices.length === 0}
                  >
                    Lanjut ke Pembayaran
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-secondary mb-4">Ringkasan Booking</h2>

              {formData.selectedServices.length > 0 ? (
                <>
                  <div className="border-b pb-4 mb-4">
                    <h3 className="font-medium text-gray-900 mb-2">Layanan Terpilih</h3>
                    {getSelectedServices().map(service => (
                      <div key={service.id} className="flex justify-between mb-2">
                        <span>{service.name}</span>
                        <span className="font-medium">{formatPrice(service.price)}</span>
                      </div>
                    ))}
                    <div className="text-sm text-gray-500 mt-3 flex items-center">
                      <FiClock className="mr-1" />
                      Total Estimasi Waktu: {getTotalDuration()} menit
                    </div>
                  </div>

                  {formData.date && formData.time && (
                    <div className="border-b pb-4 mb-4">
                      <h3 className="font-medium text-gray-900 mb-2">Jadwal</h3>
                      <div className="flex items-center text-gray-700">
                        <FiCalendar className="mr-2 text-primary" />
                        <span>
                          {formData.date && formatDate(formData.date)}
                          {formData.time && `, Pukul ${formData.time}`}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mb-2">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total Pembayaran</span>
                      <span className="text-primary">{formatPrice(getTotalPrice())}</span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mt-4 text-center">
                    Dengan melakukan booking, Anda menyetujui syarat dan ketentuan kami
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FiInfo size={32} className="mx-auto mb-2" />
                  <p>Silakan pilih layanan untuk melihat ringkasan booking</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CustomerBook; 
